import { useNavigate } from "react-router-dom";
import { Quest, artists } from "@/mocks/artistData";

interface QuestCardProps {
  quest: Quest;
}

const colorMap: Record<string, { bg: string; text: string; border: string; glow: string }> = {
  cyan: { bg: "bg-[#00f5d4]/10", text: "text-[#00f5d4]", border: "border-[#00f5d4]/30", glow: "#00f5d4" },
  magenta: { bg: "bg-[#f72585]/10", text: "text-[#f72585]", border: "border-[#f72585]/30", glow: "#f72585" },
  yellow: { bg: "bg-[#ffd60a]/10", text: "text-[#ffd60a]", border: "border-[#ffd60a]/30", glow: "#ffd60a" },
  green: { bg: "bg-[#39ff14]/10", text: "text-[#39ff14]", border: "border-[#39ff14]/30", glow: "#39ff14" },
  orange: { bg: "bg-[#ff6b35]/10", text: "text-[#ff6b35]", border: "border-[#ff6b35]/30", glow: "#ff6b35" },
  purple: { bg: "bg-[#b44fff]/10", text: "text-[#b44fff]", border: "border-[#b44fff]/30", glow: "#b44fff" },
};

const difficultyLabel: Record<string, string> = {
  Easy: "입문",
  Medium: "중급",
  Hard: "고급",
};

const difficultyColor: Record<string, string> = {
  Easy: "text-[#39ff14] bg-[#39ff14]/10 border-[#39ff14]/30",
  Medium: "text-[#ffd60a] bg-[#ffd60a]/10 border-[#ffd60a]/30",
  Hard: "text-[#f72585] bg-[#f72585]/10 border-[#f72585]/30",
};

export default function QuestCard({ quest }: QuestCardProps) {
  const navigate = useNavigate();
  const artist = artists.find((a) => a.id === quest.artistId);
  const c = colorMap[quest.color] ?? colorMap.cyan;

  return (
    <div
      onClick={() => navigate(`/quests/${quest.id}`)}
      className={`group relative bg-[#12121a] border ${c.border} rounded-2xl overflow-hidden hover:border-opacity-70 transition-all duration-300 cursor-pointer flex flex-col`}
      style={{ boxShadow: `0 0 0 0 ${c.glow}00` }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = `0 0 24px 0 ${c.glow}22`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = `0 0 0 0 ${c.glow}00`;
      }}
    >
      {/* Thumbnail */}
      <div className="relative w-full h-44 overflow-hidden">
        <img
          src={quest.thumbnail}
          alt={quest.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#12121a] via-transparent to-transparent" />

        {/* Difficulty badge */}
        <div className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-semibold border ${difficultyColor[quest.difficulty]}`}>
          {"★".repeat(quest.difficultyStars)}{"☆".repeat(5 - quest.difficultyStars)} {difficultyLabel[quest.difficulty]}
        </div>

        {/* Tag */}
        <div className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text} border ${c.border}`}>
          {quest.tag}
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-5 gap-3">
        {/* Artist info */}
        {artist && (
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full overflow-hidden border border-white/20 flex-shrink-0">
              <img src={artist.avatar} alt={artist.name} className="w-full h-full object-cover" />
            </div>
            <span className="text-xs font-semibold" style={{ color: artist.accentColor }}>
              {artist.name}
            </span>
            {artist.verified && (
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-verified-badge-fill text-xs" style={{ color: artist.accentColor }} />
              </div>
            )}
          </div>
        )}

        {/* Title */}
        <h3 className="text-white font-bold text-base leading-snug line-clamp-2 group-hover:text-white/90 transition-colors">
          {quest.title}
        </h3>

        {/* Description */}
        <p className="text-white/50 text-xs leading-relaxed line-clamp-2 flex-1">
          {quest.description}
        </p>

        {/* Progress */}
        <div className="space-y-1.5">
          <div className="flex justify-between items-center">
            <span className="text-white/40 text-xs">진행률</span>
            <span className={`text-xs font-semibold ${c.text}`}>{quest.progress}%</span>
          </div>
          <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${quest.progress}%`, background: `linear-gradient(90deg, ${c.glow}99, ${c.glow})` }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-2">
            {/* Participant avatars */}
            <div className="flex -space-x-2">
              {quest.participantAvatars.slice(0, 3).map((av, i) => (
                <div key={i} className="w-6 h-6 rounded-full overflow-hidden border-2 border-[#12121a] flex-shrink-0">
                  <img src={av} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
            <span className="text-white/40 text-xs">{quest.participants.toLocaleString()}명 참여</span>
          </div>
          <div className="flex items-center gap-1 text-white/40 text-xs">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-time-line text-xs" />
            </div>
            <span>{quest.deadline}</span>
          </div>
        </div>

        {/* Reward + CTA */}
        <div className="flex items-center justify-between pt-2 border-t border-white/10">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-coin-line text-sm" style={{ color: c.glow }} />
            </div>
            <span className="font-bold text-sm" style={{ color: c.glow }}>
              {quest.reward.toLocaleString()} {quest.rewardType}
            </span>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/quests/${quest.id}/create`); }}
            className="px-4 py-1.5 rounded-full text-xs font-semibold text-white whitespace-nowrap cursor-pointer transition-opacity hover:opacity-80"
            style={{ background: `linear-gradient(135deg, ${c.glow}cc, ${c.glow}88)` }}
          >
            캠페인 시작
          </button>
        </div>
      </div>
    </div>
  );
}
